import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cva } from 'class-variance-authority';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { joinDescribedBy } from '@/lib/aria';

/**
 * Contract: docs/FRONTEND_COMPONENT_INVENTORY.md §5.2.
 *
 * It wraps `@radix-ui/react-select`, which only understands `string`. The domain
 * value is generic (`V extends string | number`) because the same control serves
 * a numeric `dteType` (33, 34…) and a textual `status` ('draft', 'signed'…): a
 * `dteType` arriving at the backend as `"33"` is a 400, so the round-trip
 * conversion lives here and nowhere else.
 *
 * - Out: `String(value)` for Radix's controlled `value` and for each
 *   `Select.Item`'s `value`.
 * - Back: Radix's `onValueChange` hands over a `string`; the option whose
 *   `String(option.value)` matches is looked up and `onChange` is called with
 *   `option.value` **exactly as it arrived** — never with Radix's string. That
 *   way `33` goes back to being `33`, not `"33"`.
 */
export interface SelectOption<V extends string | number> {
  value: V;
  label: string;
  description?: string;
  /**
   * A short visual tag painted at the END of the option's row — `CompanySelect`
   * puts the company's `EnvironmentBadge` there, the same piece the header strip
   * shows, so an environment looks the same in the list as beside it.
   *
   * It lives OUTSIDE `Select.ItemText`, so it is neither part of the option's
   * accessible name nor copied into the closed value; it is tied to the option
   * with `aria-describedby` instead, and is still announced. A consumer that
   * wants it in the closed value paints it there with `renderValue`.
   */
  badge?: React.ReactNode;
  /**
   * The option's text with styling — `CompanySelect` sets the razón social in
   * `font-medium` and the RUT in `fg-muted`, as the header strip does. It
   * replaces `label` inside `Select.ItemText` only visually: its text content
   * must read the same as `label`, which stays the option's `textValue` for
   * typeahead. Without `renderValue`, Radix copies it into the closed value.
   */
  content?: React.ReactNode;
  disabled?: boolean;
}

export interface SelectProps<V extends string | number> {
  value: V | null;
  onChange: (value: V) => void;
  options: readonly SelectOption<V>[];
  placeholder?: string;
  disabled?: boolean;
  /** Paints the border. `FormField` sets the `aria-invalid`, not this component
   *  (the same division `Input`'s contract declares). */
  invalid?: boolean;
  id?: string;
  className?: string;
  /**
   * External id(s) to COMBINE with the trigger's internal `aria-describedby`
   * (the chosen option's note, see below) — not to replace it. It exists so a
   * container like `FormField` can tie its description and its Zod error to the
   * same `<button role="combobox">` without erasing what this component already
   * builds for itself. It can arrive as a string (`"id1 id2"`) or as an array of
   * ids, just as in `DateInput`.
   */
  'aria-describedby'?: string | readonly string[];
  /**
   * Marks the trigger as invalid. Unlike `invalid` — which only paints the
   * border — this prop IS reflected as `aria-invalid` in the DOM: it is what
   * `FormField` uses to announce the Zod error. The component has no invalid
   * state of its own to combine with, so it travels through unchanged.
   */
  'aria-invalid'?: boolean;
  /**
   * The control's accessible name, for when there is NO visible label naming it.
   *
   * In a form this is not used: `FormField` puts a real `<label>` there and ties
   * it by `id`. It is needed **inside a table**, where the column's label lives
   * in a `<th>` and the control has none of its own: a screen reader walking the
   * page by controls (not by cells) reaches four identical "combo box"es that
   * only announce their value — «Operador», «Operador», «Lector» — without
   * saying whose role that is. With four rows it is already impossible to know
   * which one is being touched.
   */
  'aria-label'?: string;
  /**
   * How the control looks.
   *
   * - `field` (the default): the usual form field — frame, background, full
   *   width and the chevron pinned to the right edge.
   * - `inline`: for a TABLE CELL. No frame and no background, as wide as its
   *   content, with the chevron beside the value. The difference is not a matter
   *   of taste: a 256×48 form field in a cell is the table's heaviest element —
   *   heavier than any datum — to hold one word, and on top of that it promises
   *   a form that does not exist (the change is saved on choosing, there is no
   *   submit). The click target does NOT shrink: it stays at `min-h-touch`, it
   *   merely stops being painted.
   * - `header`: for `AppShell`'s header strip (the `company` slot). Same
   *   layout as `field` — full width, chevron pinned to the right edge — but
   *   no border and no background: the trigger's `h-9 min-h-touch` (48px)
   *   equals the header's own `h-row` (48px), so a `field` border there
   *   coincides exactly with the header's top and bottom edges and reads as a
   *   second frame drawn on top of the header's own. The background appears
   *   only on hover, in `action-subtle` — the same "this responds" signal
   *   `inline` already uses. The click target does NOT shrink: `min-h-touch`
   *   stays at 48px, only the drawn frame goes.
   */
  variant?: 'field' | 'inline' | 'header';
  /**
   * How the chosen value is painted with the list closed. By default, the
   * option's `label` as plain text.
   *
   * It exists so a table cell shows its value with the SAME piece the
   * uneditable row beside it uses — the role's `StatusBadge`, on both user
   * screens. Without it, the «Rol» column says the same thing two different ways
   * depending on a permissions detail: a badge on one row, a form field on the
   * next, and the column stops being readable in a vertical scan.
   *
   * It receives `undefined` when nothing is chosen; returning `undefined` lets
   * Radix paint the `placeholder` or the label, as without this prop.
   */
  renderValue?: (option: SelectOption<V> | undefined) => React.ReactNode;
  /**
   * Who decides where the closed value gives way when it does not fit.
   *
   * - `truncate` (the default): the value's wrapper truncates the whole value
   *   as one line with an ellipsis at its end — what every plain label wants.
   * - `custom`: the wrapper only clips at the trigger's edge (`min-w-0
   *   overflow-hidden`, no `nowrap` and no ellipsis of its own), so a
   *   `renderValue` made of parts chooses which part truncates. The header's
   *   `CompanySelect` uses it to keep the razón social whole and let only the
   *   RUT tail give way (U14); with the wrapper truncating, the name was cut as
   *   one block with the rest.
   */
  valueOverflow?: 'truncate' | 'custom';
}

const triggerVariants = cva(
  // `min-w-0` on the trigger, not only on the value's `<span>`: the trigger is
  // itself a flex child (`AppShell`'s strip, a form row), and without this it
  // does not go below ITS content width — which, with the value in `nowrap`,
  // is the whole label. Both halves are needed: without this one the
  // truncation never kicks in and the control pushes the page sideways;
  // without the value's, the label wraps instead of truncating.
  'flex min-w-0 items-center gap-inset-sm text-body-s text-fg transition-colors focus-ring disabled:cursor-not-allowed disabled:border-border disabled:bg-page disabled:text-fg-muted data-[placeholder]:text-fg-muted',
  {
    variants: {
      variant: {
        field: 'h-9 min-h-touch w-full justify-between rounded-md border bg-surface px-inset-sm',
        // `w-fit` + the chevron beside the value (no `justify-between`): in a
        // cell, a chevron 200px from the text it governs does not read as its
        // affordance. The background appears only on hover, and in
        // `action-subtle` — the same "this responds" signal a clickable row's
        // hover already uses in `DataTable`.
        //
        // The `-mx-inset-sm` cancels its own `px-inset-sm`, so the value starts
        // on the SAME vertical as the badge in the uneditable row beside it: the
        // padding exists so the hover's background can breathe, not to shift the
        // content.
        //
        // No `min-h-touch`, and that is deliberate. The click target here is
        // ruled by the cell: `DataTable` gives it `h-row` (48px) and the
        // `<tbody>`'s `divide-y` adds 1px of collapsed border, so the content
        // box measures 47 and a 48 control stretches the row to 49 — measured.
        // The result was rows of 49px where the role can be changed and 48 where
        // it cannot, that is, a table with two row heights depending on each
        // person's permissions. With `py-inset-sm` around the badge the control
        // measures 40px: it fills the cell but for the breathing room, stays
        // well above SC 2.5.8's 24px minimum and does not deform the table. The
        // `field` variant — where the control rules its own height — does keep
        // the 48. On a coarse pointer it takes the 48 too: on a phone the select
        // leaves the row and sits in a card's button row (spec R4 of the 2026-10
        // mobile critique), where no `h-row` cell sizes it.
        inline:
          '-mx-inset-sm w-fit rounded-md border-none bg-transparent px-inset-sm py-inset-sm hover:bg-action-subtle pointer-coarse:min-h-touch',
        // Same box as `field` — `h-9 min-h-touch`, `w-full`, `justify-between`
        // — so swapping the variant does not reflow `AppShell`'s strip; only
        // the frame goes. `border-none` and `bg-transparent` instead of
        // `field`'s `border`/`bg-surface`: the header already paints its own
        // background (`app-shell.tsx`'s `<header>`), and a field's border at
        // exactly 48px coincided with the header's own top and bottom edges —
        // the defect this variant exists to fix (see the `variant` prop's
        // doc comment above). The hover background is `action-subtle`, the
        // same "this responds" signal `inline` uses for the same reason: this
        // control still does something on click, it merely draws no frame at
        // rest.
        //
        // The focus ring is drawn INSIDE the box (`-outline-offset-2`, `!`
        // because `focus-ring` sets the offset at the same specificity). The
        // trigger is the strip's full 48px, so the default 2px ring at a 2px
        // offset reached 4px past it on each side: clipped at the top and
        // crossing the header's bottom border — a focus indicator partly hidden
        // (WCAG 2.4.11).
        header:
          'h-9 min-h-touch w-full justify-between rounded-md border-none bg-transparent px-inset-sm hover:bg-action-subtle focus-visible:-outline-offset-2!',
      },
      invalid: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      // The border only exists in `field`, so its colour does too: in `inline`
      // and `header` a `border-danger` would paint a frame the variant has
      // just removed.
      { variant: 'field', invalid: true, class: 'border-danger' },
      { variant: 'field', invalid: false, class: 'border-border' },
    ],
    defaultVariants: { variant: 'field', invalid: false },
  },
);

function SelectItemRow<V extends string | number>({
  option,
}: {
  option: SelectOption<V>;
}): React.JSX.Element {
  // `Select.Item`'s accessible name comes ONLY from `Select.ItemText` (Radix
  // ties it with `aria-labelledby`, see `react-select/dist/index.js`): the
  // description lives outside that node and is tied separately with
  // `aria-describedby`, so it is never confused with the label.
  const descriptionId = React.useId();
  const badgeId = React.useId();
  const describedBy =
    [option.description ? descriptionId : null, option.badge != null ? badgeId : null]
      .filter((part): part is string => part !== null)
      .join(' ') || undefined;

  return (
    <SelectPrimitive.Item
      value={String(option.value)}
      textValue={option.label}
      disabled={option.disabled}
      aria-describedby={describedBy}
      className={cn(
        'flex cursor-pointer select-none items-start gap-inset-sm rounded-sm px-inset-sm py-stack-sm text-body-s text-fg outline-none data-[disabled]:pointer-events-none data-[disabled]:text-fg-muted data-[highlighted]:bg-action-subtle',
      )}
    >
      <span className="flex size-icon-sm shrink-0 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check aria-hidden="true" className="size-icon-sm text-action" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <span className="flex min-w-0 flex-col">
        <SelectPrimitive.ItemText>{option.content ?? option.label}</SelectPrimitive.ItemText>
        {option.description ? (
          <span id={descriptionId} className="text-caption text-fg-muted">
            {option.description}
          </span>
        ) : null}
      </span>
      {option.badge != null ? (
        // `pl-inset-sm` on top of the row's `gap`: the list is as wide as its
        // longest row, so on that row the badge sat a bare 8px from the text
        // while the others had 50px of air.
        <span id={badgeId} className="ml-auto flex shrink-0 items-center self-center pl-inset-sm">
          {option.badge}
        </span>
      ) : null}
    </SelectPrimitive.Item>
  );
}

/**
 * The selection primitive for a short list (§4 L1). For long lists with search,
 * see `Combobox`.
 *
 * ## External `aria-describedby` / `aria-invalid`
 * `FormField` injects `id`, `aria-invalid` and `aria-describedby` into the child
 * through `cloneElement`, and a child that does not DECLARE them in its props
 * loses them without anything failing: the Zod error stays visible but is not
 * tied to the control, so a screen reader does not announce it when the field is
 * focused. This component declares them and carries them to the
 * `<button role="combobox">` Radix builds (which spreads its props onto the
 * button, see `react-select/dist/index.js`); the external `aria-describedby` is
 * COMBINED with the internal one — the chosen option's note — instead of
 * replacing it.
 *
 * This holds for `CompanySelect` and `DteTypeSelect` too: they are wrappers over
 * this component and can only forward what it accepts.
 */
export function Select<V extends string | number>({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  invalid,
  id,
  className,
  variant = 'field',
  renderValue,
  valueOverflow = 'truncate',
  'aria-describedby': externalDescribedBy,
  'aria-invalid': externalInvalid,
  'aria-label': ariaLabel,
}: SelectProps<V>): React.JSX.Element {
  // Id of the node describing the CHOSEN option (see `selectionNote` below). It
  // comes from `useId()` and not from `id`, which is optional: that way the
  // trigger always has its own `aria-describedby` to combine, whether or not an
  // `id` exists.
  const selectionNoteId = React.useId();

  // How the list was last driven: by pointer or by keyboard. On close, Radix
  // hands focus back to the trigger with a scripted `focus()`, and Chrome then
  // matches `:focus-visible` even when the choice was a mouse click — the
  // keyboard ring appeared around the control and stayed, which to someone
  // using a mouse reads as a field in an error or "selected" state. So when
  // the list closes from a pointer, focus still returns to the trigger (the
  // keyboard path is untouched) but the ring is suppressed until the next key
  // press on it, or until focus leaves.
  //
  // The marker is written straight onto the DOM node, not through state:
  // Radix calls `focus()` synchronously right after `onCloseAutoFocus`
  // returns, and a state update would only commit after that — one frame of
  // ring, every time.
  const closedByPointerRef = React.useRef(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const setQuietFocus = (quiet: boolean): void => {
    if (quiet) triggerRef.current?.setAttribute('data-quiet-focus', '');
    else triggerRef.current?.removeAttribute('data-quiet-focus');
  };
  const markPointer = (): void => {
    closedByPointerRef.current = true;
  };
  const markKeyboard = (): void => {
    closedByPointerRef.current = false;
  };
  // An empty string and NOT `undefined` when nothing is chosen. Radix treats an
  // `undefined` `value` as "this Select is uncontrolled", so going from `null`
  // to a real value made it switch from uncontrolled to controlled live: React
  // loses track of the value and warns on the console. And `value: V | null` is
  // a LEGITIMATE state of the contract — "they have not chosen yet" — meaning it
  // happened to every consumer with an empty initial value, which is nearly all
  // of them: view 7's filters, the receptor, the references.
  //
  // The empty string keeps the control controlled and Radix shows the
  // placeholder all the same, because it matches no `Select.Item` (the empty
  // string is forbidden on items, not on the root).
  const stringValue = value != null ? String(value) : '';

  // What `SelectPrimitive.Value` cannot say with the list closed. Two things,
  // both invisible after choosing:
  //
  // 1. The chosen option's `description`. It is only visible with the list OPEN
  //    (`SelectItemRow`), because `SelectPrimitive.Value` renders the `label`
  //    alone — it is exactly why `CompanySelect` puts the RUT and the
  //    environment in the `label` and not in `description`.
  // 2. That the chosen option is DISABLED. Both typed wrappers
  //    (`CompanySelect`, `DteTypeSelect`) deliberately add the current `value`'s
  //    option even when it falls outside `allowed`, disabled, so as not to leave
  //    the field blank over a value that does have an owner. With the list
  //    closed that is neither seen nor heard: the trigger shows the label like
  //    any other.
  //
  // The node is mounted ALWAYS — empty when there is nothing to add — and its id
  // ALWAYS enters `aria-describedby`, for the same reason `form-field.tsx`
  // documents for its error node: a reference that appears in the same render as
  // its content is precisely the case several screen readers do not announce.
  const selectedOption = options.find(
    (candidate) => String(candidate.value) === stringValue,
  );
  const selectionNote = [
    selectedOption?.description,
    selectedOption?.disabled === true
      ? 'Esta opción ya no está disponible para elegir.'
      : undefined,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' ');

  // Internal first, external after — the same order `DateInput` and `FormField`
  // use for their own ids: what is chosen, and only then the field's description
  // and the error.
  const describedBy = joinDescribedBy(selectionNoteId, externalDescribedBy);

  function handleValueChange(next: string): void {
    const option = options.find((candidate) => String(candidate.value) === next);
    if (option) {
      onChange(option.value);
    }
  }

  return (
    <SelectPrimitive.Root
      value={stringValue}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        id={id}
        ref={triggerRef}
        onPointerDown={markPointer}
        onKeyDown={() => {
          markKeyboard();
          setQuietFocus(false);
        }}
        onBlur={() => setQuietFocus(false)}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        // Deliberately no default: `undefined` does not write the attribute, so
        // `invalid` (which only paints the border) still produces no
        // `aria-invalid`, and `FormField` remains what decides the real invalid
        // state.
        aria-invalid={externalInvalid}
        className={cn(
          triggerVariants({ variant, invalid: invalid ?? false }),
          // `!` for the same reason as the header's inset ring: `focus-ring`
          // sets the outline at the same specificity.
          'data-quiet-focus:focus-visible:outline-none!',
          className,
        )}
      >
        {/* `undefined` as children is NOT the same as empty children: Radix
            asks `children !== undefined` to decide whether to portal the chosen
            option's `ItemText` into this node (`react-select/dist/index.mjs`,
            `SelectValue`). So with no `renderValue` — or with a `renderValue`
            that returns `undefined` — the behaviour is exactly what it was:
            Radix paints the label or the placeholder. */}
        {/* The value is TRUNCATED, it never wraps. Without this a long label in
            a narrow trigger breaks over several lines and grows outside its own
            box — the control ends up taller than the frame that draws it. It
            showed up in `AppShell`'s strip on a phone: «99632700-0 · RUT
            99.632.700-0 · Sandbox» came out on three lines and the last one fell
            below the header's bottom edge. `min-w-0` is the other half: a flex
            child does not go below its content width without it, so `truncate`
            would have nothing to truncate.

            It goes in a `<span>` OF ITS OWN and not in `SelectPrimitive.Value`'s
            `className`, which is the first thing that was tried: Radix
            destructures `className` and `style` out of its props and **does not
            apply them** to the node it renders (`react-select/dist/index.mjs`,
            `SelectValue`), so the class is discarded silently — the build
            passes, the attribute never reaches the DOM and the label keeps
            wrapping just the same.

            `valueOverflow="custom"` hands the ellipsis to `renderValue`: the
            wrapper keeps `min-w-0` and only clips at the edge. */}
        <span
          data-value-overflow={valueOverflow}
          className={cn('min-w-0', valueOverflow === 'custom' ? 'overflow-hidden' : 'truncate')}
        >
          <SelectPrimitive.Value placeholder={placeholder}>
            {renderValue ? renderValue(selectedOption) : undefined}
          </SelectPrimitive.Value>
        </span>
        <SelectPrimitive.Icon asChild>
          <ChevronDown aria-hidden="true" className="size-icon-sm shrink-0 text-fg-muted" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      {/* Outside the trigger, not inside: a `combobox`'s accessible name is not
          computed from its content, but its visible text is what the tests read
          and what the person sees — an extra node inside would dirty it. Being
          `sr-only` (absolutely positioned) it does not take part in the layout
          of whatever container the select lives in either. */}
      <span id={selectionNoteId} className="sr-only">
        {selectionNote}
      </span>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          onPointerDown={markPointer}
          onPointerDownOutside={markPointer}
          onKeyDown={markKeyboard}
          onCloseAutoFocus={() => {
            setQuietFocus(closedByPointerRef.current);
          }}
          // `shadow-overlay`: the step for what floats and closes on an outside
          // click, one level above the `raised` of the card usually underneath.
          // The same one `Combobox` uses.
          className={cn(
            'z-50 max-h-[var(--radix-select-content-available-height)] overflow-hidden rounded-md border border-border bg-surface shadow-overlay',
            // The list's width comes from the trigger… as long as the trigger
            // is a field. An `inline` trigger is as wide as its value (some
            // 120px for «Operador»), and the options' `description`s —
            // «Emite y envía documentos de esta empresa, revisa los recibidos y
            // firma con su propio certificado» — cannot be read there.
            // `popup-sm` is the width role that exists precisely for a small
            // popup, and Radix's `max-w` only shrinks it when it does not fit.
            //
            // `header` sizes like `field`, not like `inline`: its options are
            // companies with an environment badge at the row's end, and a fixed
            // 400px wrapped «TCIT Producción SpA · 76.192.083-9» onto two lines
            // on a 1400px screen. Content width, capped by what is available,
            // only wraps when the screen really is that narrow.
            variant !== 'inline'
              ? // `min-w` and not `w`: the trigger's width is the list's FLOOR,
                // not its measure. With an exact `w-`, a narrow trigger — the
                // strip's company selector on a phone, 160px — gave a 160px
                // list where each option («TCIT SpA · RUT 76.304.364-9 ·
                // Certificación») came out on four lines. With the floor on the
                // trigger and the ceiling on the available width Radix
                // computes, the list opens at its content's width and only
                // wraps when it really does not fit on screen. On desktop
                // nothing changes: there the trigger is already wider than its
                // options, so the floor wins just as before.
                'min-w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-content-available-width)]'
              : 'w-popup-sm max-w-[var(--radix-select-content-available-width)]',
          )}
        >
          <SelectPrimitive.ScrollUpButton className="flex items-center justify-center py-stack-sm">
            <ChevronUp aria-hidden="true" className="size-icon-sm text-fg-muted" />
          </SelectPrimitive.ScrollUpButton>
          <SelectPrimitive.Viewport className="flex flex-col gap-stack-sm p-inset-sm">
            {options.map((option) => (
              <SelectItemRow key={String(option.value)} option={option} />
            ))}
          </SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollDownButton className="flex items-center justify-center py-stack-sm">
            <ChevronDown aria-hidden="true" className="size-icon-sm text-fg-muted" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
